/** Table invites (D-048): the player's live invites, kept in sync with `table-invite` pushes, and the join step they share. */
import { useEffect, useState } from "react";
import type { TableInvite } from "@digu/shared";
import { api } from "./api";
import { onNotify } from "./ws";

/** Takes the invited seat with the table code; resolves to the lobby path or throws the server's sentence. */
export async function joinInvite(code: string): Promise<string> {
  await api.post("/rooms/join", { code });
  return `/lobby/${encodeURIComponent(code)}`;
}
export async function declineInvite(code: string): Promise<void> {
  try { await api.post(`/invites/${encodeURIComponent(code)}/decline`, {}); } catch { /* the card goes either way */ }
}

/** Live invites for the signed-in player: loaded once, added on push, re-checked every 20 s while any are showing. */
export function useTableInvites(enabled: boolean): [TableInvite[], (code: string) => void] {
  const [invites, setInvites] = useState<TableInvite[]>([]);
  useEffect(() => {
    if (!enabled) { setInvites([]); return; }
    let alive = true;
    const load = () => { api.get<{ invites: TableInvite[] }>("/invites").then((r) => { if (alive) setInvites(r.invites); }).catch(() => undefined); };
    load();
    const off = onNotify((m) => {
      if (m.kind !== "table-invite") return;
      const inv = m.payload as TableInvite;
      if (inv && typeof inv.code === "string") setInvites((xs) => [inv, ...xs.filter((x) => x.code !== inv.code)]);
    });
    const timer = setInterval(() => { setInvites((xs) => { if (xs.length) load(); return xs; }); }, 20_000);
    return () => { alive = false; off(); clearInterval(timer); };
  }, [enabled]);
  return [invites, (code) => setInvites((xs) => xs.filter((x) => x.code !== code))];
}

/** One flow for every "Invite" on a friend: invite them to the table you are at, or set up a table with them invited. */
export async function inviteToTable(
  friend: { publicId: string; name: string; photoVersion: number },
  currentRoom: string | null,
  go: (to: string, state?: unknown) => void,
): Promise<string | null> {
  if (!currentRoom) { go(`/setup?invite=${encodeURIComponent(friend.publicId)}`, { invite: { name: friend.name, photoVersion: friend.photoVersion } }); return null; }
  await api.post(`/rooms/${encodeURIComponent(currentRoom)}/invite`, { publicId: friend.publicId });
  return `Invited ${friend.name.split(/\s+/)[0]} to your table.`;
}
